// items API — Phase 2 v2 통합 라우트 래퍼.
// GET    /api/items              목록 조회 (q·group_scope·sort 파라미터 포함)
// GET    /api/items/:id          단건 조회 (payload 포함)
// POST   /api/items              생성
// PATCH  /api/items/:id          수정
// DELETE /api/items/:id          삭제
// POST   /api/items/:id/touch    last_used_at 갱신
// POST   /api/items/:id/pin      고정
// POST   /api/items/:id/unpin    고정 해제
// POST   /api/items/:id/tags     태그 추가
// DELETE /api/items/:id/tags/:tag_id  태그 제거

import { api } from './client';

// ─── 타입 정의 ───────────────────────────────────────────────

/** items.type 12종 enum */
export type ItemType =
  | 'credential_account'
  | 'credential_api_key'
  | 'credential_server'
  | 'credential_env'
  | 'credential_auth'
  | 'note'
  | 'server'
  | 'domain'
  | 'service'
  | 'subscription'
  | 'file'
  | 'checklist';

/** 정렬 키 5종 */
export type ItemSortKey =
  | 'name'
  | 'created_at'
  | 'last_used_at'
  | 'expires_soon'
  | 'use_count';

/** 그룹 스코프 — self=단일 그룹, descendants=자손 포함 */
export type GroupScope = 'self' | 'descendants';

/** item 검색 매치 플래그 (서버 q 검색 시 응답에 포함) */
export interface ItemMatch {
  in_name: boolean;
  in_payload: boolean;
}

/** 관계 타입 */
export type RelationKind = 'hosts' | 'uses' | 'attached_to' | 'depends_on';

/** item 관계 */
export interface ItemRelation {
  id: number;
  from_id: number;
  to_id: number;
  kind: RelationKind;
}

/** item 이벤트 */
export interface ItemEvent {
  id: number;
  item_id: number;
  kind: string;
  due_at: number | null;
  meta: Record<string, unknown> | null;
}

/** listItems 응답의 item 메타 (payload 제외) */
export interface ItemMeta {
  id: number;
  type: ItemType;
  group_id: number | null;
  name: string;
  icon: string | null;
  /** 0 = false, 1 = true */
  pinned: 0 | 1;
  last_used_at: number | null;
  use_count: number;
  created_at: number;
  updated_at: number;
  /** 삭제 시각 (휴지통 목록에서만 값 존재, unix ms) */
  deleted_at?: number | null;
  /** 태그 이름 배열 */
  tags: string[];
  /** q 검색 시 매치 플래그 (q 없으면 undefined) */
  match?: ItemMatch;
}

/** 단건 조회 시 payload 포함 응답 */
export interface ItemWithPayload extends ItemMeta {
  /** 복호화된 payload 객체 */
  payload: Record<string, unknown>;
}

/** 단건 조회 응답 전체 */
export interface ItemDetailResponse {
  item: ItemWithPayload;
  payload: Record<string, unknown>;
  tags: string[];
  relations: ItemRelation[];
  events: ItemEvent[];
}

/** listItems 응답 */
export interface ListItemsResult {
  items: ItemMeta[];
  /**
   * q 검색 시 1단 후보가 200 초과면 payload 복호화 단계 스킵.
   * true일 때 payload 검색 결과 미포함 안내 표시.
   */
  payload_search_skipped?: true;
}

/** listItems 필터 옵션 */
export interface ListItemsFilter {
  /** 이름·payload 평문 검색어 */
  q?: string;
  /** item 타입 필터 */
  type?: ItemType;
  /** 그룹 ID 필터 */
  group_id?: number | null;
  /** 그룹 스코프 (기본 self) */
  group_scope?: GroupScope;
  /** 태그 이름 필터 */
  tag?: string;
  /** 핀 필터 (0=미핀, 1=핀) */
  pinned?: 0 | 1;
  /** 정렬 키 */
  sort?: ItemSortKey;
  /** 페이지 크기 (1~100) */
  limit?: number;
  /** 페이지 오프셋 */
  offset?: number;
  /** true = 미분류(group_id IS NULL)만 — 쿼리 group_id=null */
  unfiled?: boolean;
  /** 이 시각(unix ms) 이후 생성·수정 항목만 */
  updated_since?: number;
  /** true = 휴지통(삭제된 항목) 목록 */
  trash?: boolean;
}

/** 관계 추가 입력 */
export interface AddRelationInput {
  to_id: number;
  kind: RelationKind;
}

/** item 생성 입력 */
export interface CreateItemInput {
  type: ItemType | string;
  name: string;
  group_id?: number | null;
  tags?: string[];
  payload: Record<string, unknown>;
  relations?: AddRelationInput[];
  icon?: string;
  pinned?: boolean | 0 | 1;
}

/** item 수정 입력 */
export interface UpdateItemInput {
  name?: string;
  group_id?: number | null;
  icon?: string | null;
  pinned?: boolean | 0 | 1;
  tags?: string[];
  payload?: Record<string, unknown>;
}

// ─── 쿼리스트링 빌더 ─────────────────────────────────────────

function buildListQuery(filter: ListItemsFilter): string {
  const qs = new URLSearchParams();
  if (filter.q !== undefined) qs.set('q', filter.q);
  if (filter.type !== undefined) qs.set('type', filter.type);
  if (filter.group_id !== undefined && filter.group_id !== null) {
    qs.set('group_id', String(filter.group_id));
  }
  if (filter.group_scope !== undefined) qs.set('group_scope', filter.group_scope);
  if (filter.tag !== undefined) qs.set('tag', filter.tag);
  if (filter.pinned !== undefined) qs.set('pinned', String(filter.pinned));
  if (filter.sort !== undefined) qs.set('sort', filter.sort);
  if (filter.limit !== undefined) qs.set('limit', String(filter.limit));
  if (filter.offset !== undefined) qs.set('offset', String(filter.offset));
  if (filter.unfiled) qs.set('group_id', 'null');
  if (filter.updated_since !== undefined) qs.set('updated_since', String(Math.floor(filter.updated_since)));
  if (filter.trash) qs.set('trash', '1');
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ─── API 함수 ────────────────────────────────────────────────

export const itemsApi = {
  /**
   * items 목록 조회.
   * q·type·group_id·group_scope·tag·pinned·sort·limit·offset 필터 지원.
   * group_scope='descendants' → group_id 및 모든 자손 그룹의 items 포함.
   */
  listItems: (filter: ListItemsFilter = {}) =>
    api.get<ListItemsResult>(`/api/items${buildListQuery(filter)}`),

  /**
   * item 단건 조회 (payload 복호화 포함).
   */
  getItem: (id: number) => api.get<ItemDetailResponse>(`/api/items/${id}`),

  /**
   * item 생성. session 전용.
   */
  createItem: (body: CreateItemInput) => api.post<ItemWithPayload>('/api/items', body),

  /**
   * item 부분 수정. session 전용.
   */
  updateItem: (id: number, patch: UpdateItemInput) =>
    api.patch<ItemWithPayload>(`/api/items/${id}`, patch),

  /**
   * item 삭제. session 전용. 204 응답.
   */
  deleteItem: (id: number) => api.del<void>(`/api/items/${id}`),

  /**
   * last_used_at 갱신 (use_count +1). session 전용.
   */
  touchItem: (id: number) => api.post<{ ok: true }>(`/api/items/${id}/touch`),

  /**
   * pinned = 1. session 전용.
   */
  pinItem: (id: number) => api.post<{ ok: true }>(`/api/items/${id}/pin`),

  /**
   * pinned = 0. session 전용.
   */
  unpinItem: (id: number) => api.post<{ ok: true }>(`/api/items/${id}/unpin`),

  /**
   * item에 태그 추가 (태그 없으면 자동 생성). session 전용.
   */
  addItemTag: (id: number, name: string) =>
    api.post<{ ok: true; name: string }>(`/api/items/${id}/tags`, { name }),

  /**
   * item에서 태그 제거 (tag_id = item_tags.id). session 전용.
   */
  removeItemTag: (itemId: number, tagId: number) =>
    api.del<{ ok: true }>(`/api/items/${itemId}/tags/${tagId}`),

  /**
   * 휴지통 item 복원 (soft-delete → 활성). session 전용.
   * U52 restore API 연동.
   */
  restoreItem: (id: number) => api.post<{ ok: true }>(`/api/items/${id}/restore`),

  /**
   * 휴지통 item 영구 삭제. 휴지통에 있는 항목만 허용 (활성 항목은 서버 400).
   */
  purgeItem: (id: number) => api.del<void>(`/api/items/${id}/permanent`),
};
